import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { BadgeCheck, Sparkles, Check, Loader, ArrowRight, Store, Globe, Phone, MapPin, Clock, ChefHat, CreditCard, AlertCircle, Star, Tag, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import CreatePostModal from '../components/CreatePostModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Restaurant', 'Cafe', 'Bar', 'Bakery', 'Food Truck', 'Pop-up', 'Fine Dining', 'Pub', 'Catering', 'Other'];
const CUISINES = ['Italian', 'Japanese', 'Chinese', 'Indian', 'Mexican', 'Thai', 'French', 'Mediterranean', 'American', 'Korean', 'Vietnamese', 'Greek', 'Spanish', 'Other'];


// ── Post Content Component (for Business Dashboard) ──────────────────────────
const PostContentCard = ({ businessId, city }) => {
  const { toast } = useToast();
  const [type, setType] = useState('deal');
  const [form, setForm] = useState({
    title: '', description: '', image: '',
    original_price: '', discount_price: '', discount_percent: '', valid_until: '',
    event_date: '', event_time: '', event_price: '0', event_location: '',
    highlight: '', cta_text: 'Visit Us', tags: '',
  });
  const [saving, setSaving] = useState(false);
  const [myContent, setMyContent] = useState([]);
  const [open, setOpen] = useState(false);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const loadMyContent = async () => {
    const res = await fetch(`${API_URL}/api/business/content/mine`, { credentials: 'include' });
    if (res.ok) { const d = await res.json(); setMyContent(d.content || []); }
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast({ title: 'Title is required', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const body = {
        type, city: city || '', ...form,
        original_price: form.original_price ? parseFloat(form.original_price) : null,
        discount_price: form.discount_price ? parseFloat(form.discount_price) : null,
        discount_percent: form.discount_percent ? parseInt(form.discount_percent) : null,
        event_price: parseFloat(form.event_price || 0),
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      };
      const res = await fetch(`${API_URL}/api/business/content`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      toast({ title: 'Posted to Venues page!' });
      setForm({ title: '', description: '', image: '', original_price: '', discount_price: '', discount_percent: '', valid_until: '', event_date: '', event_time: '', event_price: '0', event_location: '', highlight: '', cta_text: 'Visit Us', tags: '' });
      await loadMyContent();
    } catch (err) { toast({ title: err.message, variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const deleteContent = async (id) => {
    await fetch(`${API_URL}/api/business/content/${id}`, { method: 'DELETE', credentials: 'include' });
    setMyContent(p => p.filter(x => x.content_id !== id));
    toast({ title: 'Removed' });
  };

  return (
    <Card className="border-2 border-[#FF6B35]/20">
      <CardContent className="p-5">
        <button onClick={() => { setOpen(o => !o); if (!open) loadMyContent(); }}
          className="w-full flex items-center justify-between font-bold text-gray-900">
          <span className="flex items-center gap-2"><Tag className="w-4 h-4 text-[#FF6B35]" />Post to Venues Page</span>
          <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} />
        </button>

        {open && (
          <div className="mt-4 space-y-4">
            {/* Type selector */}
            <div className="flex gap-2">
              {[['deal', '🏷️ Deal'], ['event', '📅 Event'], ['venue_post', '🏠 Highlight']].map(([v, l]) => (
                <button key={v} onClick={() => setType(v)}
                  className={`flex-1 py-2 text-xs font-semibold rounded-2xl border-2 transition-all ${type === v ? 'border-[#FF6B35] bg-[#FFF5F0] text-[#FF6B35]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                  {l}
                </button>
              ))}
            </div>

            <input value={form.title} onChange={e => f('title', e.target.value)} placeholder="Title *" className="w-full border border-gray-200 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35]" />
            <textarea value={form.description} onChange={e => f('description', e.target.value)} placeholder="Description" rows={2} className="w-full border border-gray-200 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35] resize-none" />
            <input value={form.image} onChange={e => f('image', e.target.value)} placeholder="Image URL (optional)" className="w-full border border-gray-200 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35]" />

            {type === 'deal' && (
              <div className="grid grid-cols-3 gap-2">
                <input value={form.original_price} onChange={e => f('original_price', e.target.value)} placeholder="Original $" className="border border-gray-200 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35]" />
                <input value={form.discount_price} onChange={e => f('discount_price', e.target.value)} placeholder="Sale $" className="border border-gray-200 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35]" />
                <input value={form.discount_percent} onChange={e => f('discount_percent', e.target.value)} placeholder="% Off" className="border border-gray-200 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35]" />
                <div className="col-span-3"><input type="date" value={form.valid_until} onChange={e => f('valid_until', e.target.value)} className="w-full border border-gray-200 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35]" /></div>
              </div>
            )}

            {type === 'event' && (
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={form.event_date} onChange={e => f('event_date', e.target.value)} className="border border-gray-200 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35]" />
                <input type="time" value={form.event_time} onChange={e => f('event_time', e.target.value)} className="border border-gray-200 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35]" />
                <input value={form.event_location} onChange={e => f('event_location', e.target.value)} placeholder="Venue/Location" className="border border-gray-200 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35]" />
                <input value={form.event_price} onChange={e => f('event_price', e.target.value)} placeholder="Price (0=Free)" className="border border-gray-200 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35]" />
              </div>
            )}

            {type === 'venue_post' && (
              <div className="grid grid-cols-2 gap-2">
                <input value={form.highlight} onChange={e => f('highlight', e.target.value)} placeholder="Highlight quote" className="border border-gray-200 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35]" />
                <input value={form.cta_text} onChange={e => f('cta_text', e.target.value)} placeholder="Button text" className="border border-gray-200 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35]" />
              </div>
            )}

            <input value={form.tags} onChange={e => f('tags', e.target.value)} placeholder="Tags (comma separated): Ramen, Late Night" className="w-full border border-gray-200 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35]" />

            <Button onClick={handleSubmit} disabled={saving} className="w-full bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white">
              {saving ? 'Posting...' : `Post ${type === 'deal' ? 'Deal' : type === 'event' ? 'Event' : 'Highlight'} to Venues Page`}
            </Button>

            {/* My content list */}
            {myContent.length > 0 && (
              <div className="border-t border-gray-100 pt-3">
                <p className="text-xs font-semibold text-gray-600 mb-2">Your Posts</p>
                <div className="space-y-2">
                  {myContent.map(item => (
                    <div key={item.content_id} className="flex items-center justify-between bg-gray-50 rounded-2xl p-2">
                      <div>
                        <p className="text-xs font-medium text-gray-800">{item.title}</p>
                        <p className="text-[10px] text-gray-400 capitalize">{item.type.replace('_', ' ')}</p>
                      </div>
                      <button onClick={() => deleteContent(item.content_id)} className="text-red-400 hover:text-red-600 text-xs px-2">✕</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};



// ── Promoted Post Creator for Business Dashboard ──────────────────────────────
const PromotedPostCreator = ({ isActive }) => {
  const { toast } = useToast();
  const [showPromo, setShowPromo] = useState(false);

  if (!isActive) {
    return (
      <Card className="border-2 border-dashed border-gray-200">
        <CardContent className="p-5 text-center">
          <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Sparkles className="w-6 h-6 text-gray-400" />
          </div>
          <h3 className="font-bold text-gray-700 mb-1 text-sm">Create Promoted Posts</h3>
          <p className="text-xs text-gray-500 mb-3">Subscribe to advertise in the Foodie Feed with a Sponsored label + CTA button.</p>
          <Badge variant="outline" className="border-gray-300 text-gray-500 text-xs">Requires Active Subscription</Badge>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-2 border-[#F4C430]/30 bg-gradient-to-br from-[#FFF9F0] to-white">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#F4C430] to-[#FF6B35] rounded-2xl flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-sm">Create Promotion</h3>
              <p className="text-xs text-gray-500">Appears in Foodie Feed with Sponsored label + CTA</p>
            </div>
          </div>
          <p className="text-xs text-gray-600 mb-4">
            Promoted posts reach all users in your city with a branded card, Sponsored badge, and a "Book Now" / "Order" / "View Menu" call-to-action button.
          </p>
          <Button
            data-testid="create-promo-btn"
            onClick={() => setShowPromo(true)}
            className="w-full bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white rounded-2xl font-semibold gap-2"
          >
            <Sparkles className="w-4 h-4" />Create Promoted Post
          </Button>
        </CardContent>
      </Card>
      {showPromo && (
        <CreatePostModal
          isPromo={true}
          onClose={() => setShowPromo(false)}
          onPostCreated={() => { toast({ title: 'Promo post live!', description: 'Appearing in Foodie Feed.' }); setShowPromo(false); }}
        />
      )}
    </>
  );
};


const BusinessPortal = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [myBusiness, setMyBusiness] = useState(null);
  const [bizLoading, setBizLoading] = useState(false);
  const [view, setView] = useState('landing'); // landing | register | dashboard | payment-success | payment-pending
  const [paymentPolling, setPaymentPolling] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    name: '', tagline: '', description: '', cuisine: '', category: 'Restaurant',
    address: '', city: '', suburb: '', country: '', phone: '', website: '', email: '',
    cover_image: '', price_range: '$$', hours: '', tags: '',
  });

  // Handle returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && user) {
      setView('payment-pending');
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, user]); // eslint-disable-line

  // Load existing business
  useEffect(() => {
    if (user) loadMyBusiness();
  }, [user]); // eslint-disable-line

  const loadMyBusiness = async () => {
    setBizLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/business/me`, { credentials: 'include' });
      if (res.ok) {
        const biz = await res.json();
        setMyBusiness(biz);
        setForm({
          name: biz.name || '', tagline: biz.tagline || '',
          description: biz.description || '', cuisine: biz.cuisine || '',
          category: biz.category || 'Restaurant', address: biz.address || '',
          city: biz.city || '', suburb: biz.suburb || '', country: biz.country || '',
          phone: biz.phone || '', website: biz.website || '', email: biz.email || '',
          cover_image: biz.cover_image || '', price_range: biz.price_range || '$$',
          hours: biz.hours || '', tags: (biz.tags || []).join(', '),
        });
        setView('dashboard');
      } else {
        setView(user ? 'register' : 'landing');
      }
    } catch { setView('register'); } finally {
      setBizLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 8;
    setPaymentPolling(true);
    try {
      const res = await fetch(`${API_URL}/api/business/payment/status/${sessionId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPaymentStatus(data);
        if (data.payment_status === 'paid') {
          setPaymentPolling(false);
          setView('payment-success');
          await loadMyBusiness();
          return;
        }
      }
    } catch { /* keep polling */ }
    if (attempts < maxAttempts) {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2500);
    } else {
      setPaymentPolling(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaveLoading(true);
    setError('');
    try {
      const payload = {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      };
      const res = await fetch(`${API_URL}/api/business/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Save failed');
      }
      const biz = await res.json();
      setMyBusiness(biz);
      setView('dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCheckout = async (tier = 'standard') => {
    setCheckoutLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/business/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ origin_url: window.location.origin, tier }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Checkout failed');
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setCheckoutLoading(false);
    }
  };

  const isActive = myBusiness?.is_active || myBusiness?.subscription?.status === 'active';

  // Days remaining calculation
  const daysLeft = (() => {
    const exp = myBusiness?.subscription?.expires_at;
    if (!exp || !isActive) return null;
    try {
      const diff = new Date(exp) - new Date();
      return Math.ceil(diff / (1000 * 60 * 60 * 24));
    } catch { return null; }
  })();
  const expiringSoon = daysLeft !== null && daysLeft <= 7 && daysLeft > 0;

  if (authLoading || bizLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#FF6B35]/30 border-t-[#FF6B35] rounded-full animate-spin" />
      </div>
    );
  }

  // ── Landing page ────────────────────────────────────────────────────────────
  if (view === 'landing' || (!user && view !== 'landing')) {
    return (
      <>
        <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
          {/* Hero */}
          <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1600)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <div className="relative max-w-4xl mx-auto px-4 text-center">
              <div className="inline-flex items-center gap-2 bg-[#FF6B35]/20 border border-[#FF6B35]/30 rounded-full px-4 py-1.5 mb-6">
                <Store className="w-4 h-4 text-[#FF6B35]" />
                <span className="text-sm font-medium text-[#FF8C42]">FoodQuest Business</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-extrabold mb-4 leading-tight">
                Grow Your Venue with<br /><span className="text-[#FF8C42]">FoodQuest</span>
              </h1>
              <p className="text-white/70 text-xl mb-10 max-w-2xl mx-auto">
                Get discovered by thousands of food lovers. List your restaurant, cafe, or food business on FoodQuest's Venue page.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  data-testid="get-started-business-btn"
                  onClick={() => user ? setView('register') : setShowAuthModal(true)}
                  className="bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] text-white text-lg px-8 py-6 h-auto rounded-2xl gap-2 font-semibold"
                >
                  List My Business <ArrowRight className="w-5 h-5" />
                </Button>
                <Link to="/venue">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6 h-auto rounded-2xl">
                    Browse Venues
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="max-w-4xl mx-auto px-4 py-16">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-3">Simple, Transparent Pricing</h2>
            <p className="text-center text-gray-500 mb-10">One plan. Everything included.</p>

            <div className="max-w-md mx-auto">
              <Card className="border-2 border-[#FF6B35] overflow-hidden shadow-xl">
                <div className="bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] p-6 text-white text-center">
                  <p className="text-sm font-medium uppercase tracking-widest opacity-90 mb-2">Business Membership</p>
                  <div className="flex items-end justify-center gap-1">
                    <span className="text-6xl font-extrabold">$10</span>
                    <span className="text-xl mb-3 opacity-80">/month</span>
                  </div>
                  <p className="text-white/80 text-sm">Cancel anytime</p>
                </div>
                <CardContent className="p-6 space-y-3">
                  {[
                    'Verified badge on your listing',
                    'Featured in the Venue page',
                    'Full business profile (photos, menu, hours)',
                    'Direct booking & delivery links',
                    'AI-powered review summary',
                    'Reach food lovers near you',
                    'Priority support',
                  ].map(f => (
                    <div key={f} className="flex items-center gap-3">
                      <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-green-600" />
                      </div>
                      <span className="text-sm text-gray-700">{f}</span>
                    </div>
                  ))}
                  <Button
                    onClick={() => user ? setView('register') : setShowAuthModal(true)}
                    className="w-full mt-6 bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] text-white h-12 font-semibold text-base rounded-2xl"
                  >
                    Get Started <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Social proof */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14 text-center">
              {[
                { icon: '🍽️', stat: '10,000+', label: 'Monthly food explorers' },
                { icon: '🌍', stat: 'Global', label: 'Reach any city worldwide' },
                { icon: '⚡', stat: '<24h', label: 'Listing goes live' },
              ].map(({ icon, stat, label }) => (
                <div key={stat} className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
                  <div className="text-3xl mb-2">{icon}</div>
                  <div className="text-2xl font-bold text-[#FF6B35] mb-1">{stat}</div>
                  <div className="text-sm text-gray-500">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} defaultMode="register" />}
      </>
    );
  }

  // ── Register / Edit profile ─────────────────────────────────────────────────
  if (view === 'register') {
    return (
      <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
        <div className="bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] text-white py-10">
          <div className="max-w-3xl mx-auto px-4">
            <h1 className="text-3xl font-bold mb-1">Set Up Your Business Listing</h1>
            <p className="text-white/80">Fill in your details — you can always edit them later.</p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
          {/* Basic Info */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2"><Store className="w-5 h-5 text-[#FF6B35]" />Business Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Business Name *</label>
                  <Input data-testid="biz-name-input" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="e.g. Tokyo Ramen House" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Tagline</label>
                  <Input value={form.tagline} onChange={e => setForm(p => ({...p, tagline: e.target.value}))} placeholder="e.g. Authentic ramen since 2015" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Category</label>
                  <Select value={form.category} onValueChange={v => setForm(p => ({...p, category: v}))}>
                    <SelectTrigger data-testid="biz-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Cuisine</label>
                  <Select value={form.cuisine} onValueChange={v => setForm(p => ({...p, cuisine: v}))}>
                    <SelectTrigger><SelectValue placeholder="Select cuisine" /></SelectTrigger>
                    <SelectContent>{CUISINES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Price Range</label>
                  <Select value={form.price_range} onValueChange={v => setForm(p => ({...p, price_range: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['$', '$$', '$$$', '$$$$'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Description</label>
                  <textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))}
                    placeholder="Tell food lovers what makes your venue special..."
                    rows={3} className="w-full text-sm border border-gray-200 rounded-2xl px-3 py-2.5 focus:outline-none focus:border-[#FF6B35] resize-none" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Location */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2"><MapPin className="w-5 h-5 text-[#FF6B35]" />Location</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-3">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Street Address</label>
                  <Input data-testid="biz-address-input" value={form.address} onChange={e => setForm(p => ({...p, address: e.target.value}))} placeholder="123 Main St" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Suburb / Area</label>
                  <Input value={form.suburb} onChange={e => setForm(p => ({...p, suburb: e.target.value}))} placeholder="e.g. Bondi Beach" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">City *</label>
                  <Input value={form.city} onChange={e => setForm(p => ({...p, city: e.target.value}))} placeholder="e.g. Sydney" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Country</label>
                  <Input value={form.country} onChange={e => setForm(p => ({...p, country: e.target.value}))} placeholder="e.g. Australia" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact & Hours */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2"><Phone className="w-5 h-5 text-[#FF6B35]" />Contact & Hours</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Phone</label>
                  <Input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} placeholder="+1 (555) 000-0000" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Business Email</label>
                  <Input value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="info@yourvenue.com" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Website</label>
                  <Input value={form.website} onChange={e => setForm(p => ({...p, website: e.target.value}))} placeholder="https://yourvenue.com" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Opening Hours</label>
                  <Input value={form.hours} onChange={e => setForm(p => ({...p, hours: e.target.value}))} placeholder="Mon–Sun: 11am–10pm" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Cover Image URL</label>
                  <Input value={form.cover_image} onChange={e => setForm(p => ({...p, cover_image: e.target.value}))} placeholder="https://your-image.jpg" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Tags (comma separated)</label>
                  <Input value={form.tags} onChange={e => setForm(p => ({...p, tags: e.target.value}))} placeholder="e.g. Ramen, Late Night, Date Night" />
                </div>
              </div>
            </CardContent>
          </Card>

          {error && <p className="text-sm text-red-600 bg-red-50 px-4 py-3 rounded-2xl">{error}</p>}

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setView('landing')} className="flex-1">Cancel</Button>
            <Button data-testid="save-business-btn" onClick={handleSaveProfile} disabled={saveLoading || !form.name || !form.city}
              className="flex-2 flex-1 bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] text-white font-semibold">
              {saveLoading ? <><Loader className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save & Continue →'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Payment pending ─────────────────────────────────────────────────────────
  if (view === 'payment-pending') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 max-w-sm">
          <div className="w-16 h-16 border-4 border-[#FF6B35]/30 border-t-[#FF6B35] rounded-full animate-spin mx-auto mb-5" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Processing Payment</h2>
          <p className="text-gray-500">Verifying your payment with Stripe...</p>
        </div>
      </div>
    );
  }

  // ── Payment success ─────────────────────────────────────────────────────────
  if (view === 'payment-success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 max-w-sm">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <BadgeCheck className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to FoodQuest Business!</h2>
          <p className="text-gray-500 mb-6">Your venue is now live and visible to food lovers near you.</p>
          <div className="flex gap-3">
            <Link to="/venue" className="flex-1">
              <Button variant="outline" className="w-full border-[#FF6B35] text-[#FF6B35]">View Venue Page</Button>
            </Link>
            <Button onClick={() => setView('dashboard')} className="flex-1 bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white">
              My Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Dashboard ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <div className={`text-white py-10 ${isActive ? 'bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35]' : 'bg-gradient-to-r from-gray-700 to-gray-900'}`}>
        <div className="max-w-5xl mx-auto px-4 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              {isActive ? (
                <Badge className="bg-white/20 text-white border-white/30 gap-1">
                  <BadgeCheck className="w-3.5 h-3.5" />Active Membership
                </Badge>
              ) : (
                <Badge className="bg-yellow-400/20 text-yellow-200 border-yellow-400/30 gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />Inactive — Listing Hidden
                </Badge>
              )}
            </div>
            <h1 className="text-3xl font-extrabold mb-1">{myBusiness?.name || 'My Business'}</h1>
            <p className="text-white/75 text-sm">{myBusiness?.category} · {[myBusiness?.suburb, myBusiness?.city].filter(Boolean).join(', ')}</p>
          </div>
          {isActive ? (
            <div className="bg-white/15 backdrop-blur rounded-2xl px-5 py-3 text-center">
              <p className="text-xs text-white/70 mb-1">Membership expires</p>
              <p className="font-bold">{myBusiness?.subscription?.expires_at ? new Date(myBusiness.subscription.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</p>
            </div>
          ) : null}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Subscription card */}
          <div className="lg:col-span-1 space-y-4">
            <Card className={`border-2 ${isActive ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50'}`}>
              <CardContent className="p-5">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-[#FF6B35]" />Subscription
                </h3>
                {isActive ? (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-sm font-semibold text-green-700">Active & Listed</span>
                    </div>

                    {expiringSoon ? (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-3 mb-3">
                        <p className="text-xs font-semibold text-yellow-700">⚠️ Expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}!</p>
                        <p className="text-xs text-yellow-600 mt-0.5">Renew now to avoid interruption.</p>
                      </div>
                    ) : daysLeft !== null ? (
                      <p className="text-xs text-gray-500 mb-3">{daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining</p>
                    ) : (
                      <p className="text-xs text-gray-500 mb-3">Your venue is visible to all users.</p>
                    )}

                    <div className="space-y-2">
                      <Link to="/venue">
                        <Button variant="outline" className="w-full text-sm border-green-300 text-green-700 hover:bg-green-100">
                          View My Listing →
                        </Button>
                      </Link>
                      <Button
                        data-testid="renew-subscription-btn"
                        onClick={handleCheckout}
                        disabled={checkoutLoading}
                        variant="outline"
                        className="w-full text-sm border-[#FF6B35] text-[#FF6B35] hover:bg-[#FFF5F0]"
                      >
                        {checkoutLoading ? <><Loader className="w-4 h-4 mr-2 animate-spin" />Redirecting...</> : 'Renew Subscription'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-600 mb-4">Subscribe for <strong>$10/month</strong> to make your venue visible to food lovers.</p>
                    {error && <p className="text-xs text-red-600 mb-3">{error}</p>}
                    <Button
                      data-testid="subscribe-btn"
                      onClick={handleCheckout}
                      disabled={checkoutLoading}
                      className="w-full bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white font-semibold"
                    >
                      {checkoutLoading ? <><Loader className="w-4 h-4 mr-2 animate-spin" />Redirecting...</> : <><Sparkles className="w-4 h-4 mr-2" />Subscribe — $10/month</>}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>


              {/* Featured tier upgrade */}
              {isActive && !myBusiness?.is_featured && (
                <Card className="border-2 border-yellow-200 bg-gradient-to-br from-yellow-50 to-orange-50">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">⭐</span>
                      <h3 className="font-bold text-gray-900 text-sm">Go Featured</h3>
                      <span className="ml-auto text-sm font-bold text-[#FF6B35]">$25/mo</span>
                    </div>
                    <p className="text-xs text-gray-600 mb-3">Appear at the top of every Venues search. Get 5× more visibility.</p>
                    <Button data-testid="upgrade-featured-btn" onClick={() => handleCheckout('featured')} disabled={checkoutLoading}
                      size="sm" className="w-full bg-gradient-to-r from-[#F4C430] to-[#FF8C42] text-white text-xs">
                      {checkoutLoading ? '...' : 'Upgrade to Featured — $25/mo'}
                    </Button>
                  </CardContent>
                </Card>
              )}

            {/* Quick stats */}
            <Card>
              <CardContent className="p-5">
                <h3 className="font-bold text-gray-900 mb-3">Quick Stats</h3>
                {[
                  { label: 'Category', value: myBusiness?.category || '—' },
                  { label: 'Cuisine', value: myBusiness?.cuisine || '—' },
                  { label: 'Price Range', value: myBusiness?.price_range || '—' },
                  { label: 'City', value: myBusiness?.city || '—' },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between py-1.5 border-b border-gray-50 last:border-0">
                    <span className="text-sm text-gray-500">{label}</span>
                    <span className="text-sm font-medium text-gray-900">{value}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Post Content — business tagging for Venues page */}
          <div className="lg:col-span-2 space-y-4">
            <PostContentCard businessId={myBusiness?.business_id} city={myBusiness?.city} />

            {/* Promoted Post in Feed */}
            <PromotedPostCreator isActive={isActive} />


            {/* Profile preview */}
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-900">Listing Preview</h3>
                  <Button variant="outline" size="sm" onClick={() => setView('register')} className="border-[#FF6B35] text-[#FF6B35] hover:bg-[#FFF5F0]">
                    Edit Profile
                  </Button>
                </div>

                {/* Mini preview card */}
                <div className="rounded-2xl overflow-hidden border border-gray-200">
                  <div className="h-40 relative">
                    {myBusiness?.cover_image ? (
                      <img loading="lazy" src={myBusiness.cover_image} alt={myBusiness.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#F4C430] via-[#FF8C42] to-[#FF6B35] flex items-center justify-center text-4xl">🍽️</div>
                    )}
                    {isActive && (
                      <div className="absolute top-3 left-3 bg-white/95 rounded-full px-2.5 py-1 flex items-center gap-1 shadow-sm">
                        <BadgeCheck className="w-3.5 h-3.5 text-[#FF6B35]" />
                        <span className="text-xs font-semibold text-[#FF6B35]">Verified</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h4 className="font-bold text-gray-900 text-lg">{myBusiness?.name}</h4>
                    {myBusiness?.tagline && <p className="text-xs text-[#FF6B35] font-medium mb-2">{myBusiness.tagline}</p>}
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {myBusiness?.cuisine && <Badge variant="outline" className="text-xs border-[#FF6B35] text-[#FF6B35]">{myBusiness.cuisine}</Badge>}
                      {(myBusiness?.tags || []).slice(0, 3).map(t => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}
                    </div>
                    {myBusiness?.description && <p className="text-sm text-gray-600 line-clamp-2">{myBusiness.description}</p>}
                    {myBusiness?.city && (
                      <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-[#FF6B35]" />
                        {[myBusiness.suburb, myBusiness.city, myBusiness.country].filter(Boolean).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessPortal;
